# MCP Overview

Connect FHIRfly to AI assistants using the Model Context Protocol.

## What is MCP?

The [Model Context Protocol (MCP)](https://modelcontextprotocol.io) is an **open standard** that allows AI assistants to securely access external tools and data sources. It's not tied to any specific AI provider—any application that implements the MCP specification can use FHIRfly as a tool provider.

FHIRfly's MCP integration lets AI assistants look up healthcare reference data directly, with full provenance and citation support.

## Benefits

- **Real-time data**: AI gets current NDC, NPI, and other reference data
- **Accurate citations**: Full provenance information for reliable sourcing
- **Seamless experience**: No copy-pasting between apps
- **Secure access**: Uses your API credentials with proper scoping

## How It Works

<img src="/diagrams/mcp-overview.svg" alt="MCP flow: MCP Client connects to MCP Server, which connects to FHIRfly API for healthcare reference data" style={{maxWidth: '100%', margin: '1.5rem 0'}} />

1. Configure your MCP client with FHIRfly credentials
2. The AI can call FHIRfly tools when you ask healthcare questions
3. FHIRfly returns structured data with provenance
4. The AI uses this data to provide accurate, cited responses

## Available Tools

When connected via MCP, your AI assistant has access to 39 tools across seven categories:

### Lookup Tools

Direct lookups when you have an exact code or identifier:

| Tool | Description |
|------|-------------|
| `ndc_get` | Look up a drug by NDC code |
| `npi_get` | Look up a provider by NPI number |
| `rxnorm_get` | Look up a drug by RxCUI |
| `loinc_get` | Look up a lab test by LOINC code |
| `icd10_get` | Look up a diagnosis/procedure by ICD-10 code |
| `cvx_get` | Look up a vaccine by CVX code |
| `mvx_get` | Look up a vaccine manufacturer by MVX code |
| `fda_label_lookup` | Look up an FDA drug label by NDC or RxCUI |
| `snomed_get` | Look up a clinical concept by SNOMED CT ID |
| `npi_connectivity` | Look up connectivity options (FHIR endpoints) for a provider |

### Search Tools

Full-text search when you need to find codes:

| Tool | Description |
|------|-------------|
| `ndc_search` | Search drug products by name, ingredient, or dosage form |
| `npi_search` | Search providers by name, specialty, or location |
| `rxnorm_search` | Search drug terminology by name or ingredient |
| `loinc_search` | Search lab codes by component, class, or system |
| `icd10_search` | Search diagnosis/procedure codes by description |
| `cvx_search` | Search vaccine codes by name or type |
| `mvx_search` | Search vaccine manufacturers by name |
| `fda_label_search` | Search drug labels by name, substance, or manufacturer |
| `snomed_search` | Search SNOMED CT IPS clinical concepts |

### FDA Label Specialized Tools

Extract specific sections from FDA drug labels:

| Tool | Description |
|------|-------------|
| `fda_label_safety` | Get safety information (boxed warnings, contraindications, adverse reactions) |
| `fda_label_interactions` | Get drug interaction information |
| `fda_label_dosing` | Get dosage and administration information |
| `fda_label_sections` | Get specific label sections by name |

### Cross-Terminology Tools

Navigate between terminology systems:

| Tool | Description |
|------|-------------|
| `snomed_mappings` | Find ICD-10, RxNorm, NDC codes that map to a SNOMED concept |

### SMA Endpoint Directory Tools

State Medicaid Agency FHIR endpoint implementation data:

| Tool | Description |
|------|-------------|
| `sma_list_states` | List states with Medicaid FHIR endpoint implementation status |
| `sma_get_state` | Get full SMA FHIR endpoint details for a specific state |
| `sma_stats` | Get aggregate SMA implementation statistics across all states |

### Batch Tools

Look up multiple codes in a single request for efficient bulk operations:

| Tool | Description |
|------|-------------|
| `ndc_batch` | Look up multiple NDC codes in one request (max 500) |
| `npi_batch` | Look up multiple NPI numbers in one request (max 100) |
| `icd10_batch` | Look up multiple ICD-10 codes in one request (max 100) |
| `rxnorm_batch` | Look up multiple RxCUI codes in one request (max 100) |
| `loinc_batch` | Look up multiple LOINC codes in one request (max 100) |
| `cvx_batch` | Look up multiple CVX codes in one request (max 100) |
| `mvx_batch` | Look up multiple MVX codes in one request (max 100) |
| `fda_label_batch` | Look up multiple FDA drug labels in one request (max 50) |

### Claims Intelligence Tools

Medicare claims compliance and reimbursement data:

| Tool | Description |
|------|-------------|
| `ncci_validate` | Check if two HCPCS/CPT codes can be billed together (NCCI PTP edits) |
| `mue_lookup` | Look up maximum units of service for a HCPCS code (MUE limits) |
| `pfs_lookup` | Look up Medicare Physician Fee Schedule RVU data and payment rates |
| `coverage_check` | Check LCD/NCD coverage determinations for a HCPCS code |

## Example Conversation

**You**: What are the active ingredients in NDC 0069-0151-01?

**AI**: Let me look that up for you.

*[Uses ndc_get tool]*

Based on the FDA NDC Directory, NDC 0069-0151-01 is **Lipitor** manufactured by Pfizer. The active ingredient is:

- **Atorvastatin Calcium** - 10 mg

This is a film-coated tablet for oral administration used to treat high cholesterol.

*Source: FDA NDC Directory, updated January 2026*

## Getting Started

Choose your setup path:

- **[Claude Desktop Setup](/docs/mcp/claude-desktop)** — Step-by-step configuration for Claude Desktop
- **[HTTP API Reference](/docs/mcp/api-reference)** — Build custom MCP integrations

## Supported MCP Clients

MCP is an open standard supported by a growing ecosystem:

- **Claude Desktop** (macOS, Windows) — Full support via local server or HTTP
- **Custom Applications** — Any app implementing the [MCP specification](https://modelcontextprotocol.io)

See [How It Works](/docs/mcp/how-it-works) for technical details.
